import { Injectable } from '@angular/core';
import { RequestService } from './request.service';
import { BehaviorSubject, Observable } from 'rxjs';
import moment from 'moment';
import { Currency } from '../types/currency.type';

interface CachedCurrency {
  currency: Currency;
  expireDatetime: string;
}

@Injectable({
  providedIn: 'root',
})
export class CurrencyService {

  public defaultCurrency = new BehaviorSubject<Currency | undefined>(undefined);
  private readonly CACHE_KEY = 'system_currency';

  constructor(
    private request: RequestService
  ) {
    this.initializeDisplayCurrency();
  }

  private initializeDisplayCurrency(): void {
    // Check cache first
    const cached = this.getCachedCurrency();

    if (cached) {
      this.defaultCurrency.next(cached);
    } else {
      this.request.get(['partner', 'currency', 'default'])
        .subscribe((currency: Currency) => {
          this.defaultCurrency.next(currency);
          this.cacheCurrency(currency);
        });
    }
  }

  private getCachedCurrency(): Currency | null {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      if (!cached) {
        return null;
      }

      const { currency, expireDatetime }: CachedCurrency = JSON.parse(cached);
      // Check if cache is still valid
      if (moment().isBefore(moment(expireDatetime))) {
        return currency;
      }

      // Cache expired, remove it
      localStorage.removeItem(this.CACHE_KEY);
      return null;
    } catch {
      return null;
    }
  }

  private cacheCurrency(currency: Currency): void {
    const cacheData: CachedCurrency = {
      currency: currency,
      expireDatetime: moment().add(1, 'day').startOf('day').toISOString(),
    };
    localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheData));
  }

  convertToDefaultCurrency$(amount: number, fromCurrency: string): Observable<{ amount: number, currency: string }> {
    return this.request.get(['partner', 'currency', 'default', 'convert'], {
      params: {
        amount: amount,
        fromCurrency: fromCurrency
      }
    });
  }
}